import type { MapAdapter } from '../types';
import type { MapLibreAdapter } from '../adapters/MapLibreAdapter';

const TILE_SERVER = 'http://localhost:8001';
const YEAR        = 2024;

// Palette mirrors tile_server.py: navy → dark → gold
const LEGEND_GRADIENT = 'linear-gradient(to right, #214469, #080d18, #D4B346)';

interface TileResponse {
  url:  string;
  band: string;
  year: number;
  min:  number;
  max:  number;
}

/**
 * Listens for `sm:bandselect` custom events and renders the chosen
 * AlphaEarth embedding band as a Leaflet TileLayer fetched from the
 * local GEE tile server (tile_server.py).
 *
 * Mount once in main.ts; it wires itself to the root element.
 */
export class BandOverlay {
  private layerId: string | null = null;
  private badge:   HTMLElement;
  private serverOk: boolean | null = null;  // null = not yet checked

  constructor(
    private readonly root: HTMLElement,
    private readonly map: MapAdapter,
  ) {
    this.badge = this.createBadge();
    root.appendChild(this.badge);

    root.addEventListener('sm:bandselect', (e) => {
      const { band } = (e as CustomEvent<{ band: string }>).detail;
      this.loadBand(band);
    });
  }

  // ── Badge UI ──────────────────────────────────────────────────────────────

  private createBadge(): HTMLElement {
    const el = document.createElement('div');
    el.id = 'sm-band-badge';
    el.style.display = 'none';
    el.innerHTML = `
      <div class="sm-band-badge-header">
        <span class="sm-band-badge-name" id="sm-band-badge-name">—</span>
        <span class="sm-band-badge-year">${YEAR}</span>
        <button class="sm-band-badge-close" id="sm-band-badge-close" title="Remove overlay">&#x2715;</button>
      </div>
      <div class="sm-band-badge-sub" id="sm-band-badge-sub">AlphaEarth embedding band</div>
      <div class="sm-band-legend">
        <span class="sm-band-legend-val" id="sm-band-legend-min">—</span>
        <div class="sm-band-legend-bar" style="background:${LEGEND_GRADIENT};"></div>
        <span class="sm-band-legend-val" id="sm-band-legend-max">—</span>
      </div>`;

    el.querySelector('#sm-band-badge-close')!.addEventListener('click', () => {
      this.clear();
    });
    return el;
  }

  private showBadge(band: string, min: number, max: number): void {
    this.badge.style.display = 'block';
    this.badge.querySelector<HTMLElement>('#sm-band-badge-name')!.textContent = band;
    this.badge.querySelector<HTMLElement>('#sm-band-legend-min')!.textContent = min.toFixed(3);
    this.badge.querySelector<HTMLElement>('#sm-band-legend-max')!.textContent = max.toFixed(3);
    this.badge.querySelector<HTMLElement>('#sm-band-badge-sub')!.textContent =
      'AlphaEarth 2024 · embedding band · navy→gold';
  }

  private showError(msg: string): void {
    this.badge.style.display = 'block';
    this.badge.querySelector<HTMLElement>('#sm-band-badge-name')!.textContent = 'Error';
    this.badge.querySelector<HTMLElement>('#sm-band-badge-sub')!.textContent = msg;
    this.badge.querySelector<HTMLElement>('#sm-band-legend-min')!.textContent = '';
    this.badge.querySelector<HTMLElement>('#sm-band-legend-max')!.textContent = '';
  }

  private showLoading(band: string): void {
    this.badge.style.display = 'block';
    this.badge.querySelector<HTMLElement>('#sm-band-badge-name')!.textContent = band;
    this.badge.querySelector<HTMLElement>('#sm-band-badge-sub')!.textContent = 'Loading from GEE…';
    this.badge.querySelector<HTMLElement>('#sm-band-legend-min')!.textContent = '';
    this.badge.querySelector<HTMLElement>('#sm-band-legend-max')!.textContent = '';
  }

  // ── Layer management ──────────────────────────────────────────────────────

  private clear(): void {
    if (this.layerId) {
      (this.map as unknown as MapLibreAdapter).removeRasterSource(this.layerId);
      this.layerId = null;
    }
    this.badge.style.display = 'none';

    // Deselect all band cards
    document.querySelectorAll<HTMLElement>('.sm-svm-dim-card.selected')
      .forEach(c => c.classList.remove('selected'));
  }

  private async loadBand(band: string): Promise<void> {
    // If same band re-clicked, toggle off
    const currentName = this.badge.querySelector<HTMLElement>('#sm-band-badge-name')?.textContent;
    if (currentName === band && this.layer) {
      this.clear();
      return;
    }

    this.clear();
    this.showLoading(band);

    // Check if tile server is running (once per session)
    if (this.serverOk === null) {
      try {
        const hres = await fetch(`${TILE_SERVER}/health`, { signal: AbortSignal.timeout(2000) });
        this.serverOk = hres.ok;
      } catch {
        this.serverOk = false;
      }
    }

    if (!this.serverOk) {
      this.showError('Start tile_server.py on port 8001');
      return;
    }

    try {
      const res  = await fetch(`${TILE_SERVER}/gee-tile?band=${band}&year=${YEAR}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        this.showError(err.error ?? 'Server error');
        return;
      }

      const data: TileResponse = await res.json();

      this.layerId = `band-${data.band}`;
      (this.map as unknown as MapLibreAdapter).addRasterSource(this.layerId, data.url, 0.72);
      this.showBadge(data.band, data.min, data.max);
      this.serverOk = true;   // still ok

    } catch (err) {
      this.showError('Could not reach tile server');
      this.serverOk = null;   // retry health check next time
    }
  }
}
